/**
 * Les chemins d'URL, dans les deux langues.
 *
 * Le produit est bilingue par fondation — chaque chaîne existe en français et
 * en anglais, et le typage rend l'oubli impossible — mais ses URL étaient
 * restées en français : un opérateur anglophone lisait « Bookings » dans une
 * page servie sur `/reservations`, et un visiteur anglophone recevait un lien
 * vers `/enregistrement`.
 *
 * **Les deux variantes sont TOUJOURS servies.** Ce n'est pas de la
 * complaisance : le QR-code d'enregistrement est imprimé et collé sur un mur,
 * et les liens de désabonnement sont dans des courriels déjà partis. Un chemin
 * public qu'on renomme sans garder l'ancien casse du papier et des boîtes de
 * réception, et ça ne se voit qu'au premier appel d'un visiteur.
 *
 * Ce qui change, c'est le chemin qu'on ÉMET : la navigation et les liens
 * suivent la langue de l'interface, ou celle du destinataire pour un courriel.
 *
 * Certains chemins sont identiques dans les deux langues — `/scan`, `/studio`,
 * `/experiences`. Ils sont quand même déclarés ici, parce que la question
 * « où est la liste des chemins » ne doit avoir qu'une réponse.
 *
 * Il n'y a PAS de `/sites` : un site est une page d'expérience, donc il vit
 * dans l'onglet « Sites web » de l'expérience qu'il vend. Un écran séparé
 * obligeait à choisir l'expérience dans une liste déroulante alors qu'on
 * venait d'elle.
 */

export type PathLocale = 'fr' | 'en'

/** Les écrans du back-office. */
export const APP_PATHS = {
  /*
   * La racine est le TABLEAU DE BORD, et son contenu dépend du compte.
   *
   * Un compte du studio y voit la plateforme, un exploitant y voit ses lieux ;
   * le nom, lui, ne change pas. Deux libellés pour la même touche auraient
   * obligé chacun à réapprendre le mot de l'autre au téléphone — « va dans
   * Mes lieux », « je n'ai pas ça ».
   */
  dashboard: { fr: '/', en: '/' },
  /*
   * AUJOURD'HUI : la première page d'un LIEU, celle qu'on ouvre en y entrant.
   *
   * C'était la racine, et c'est ce qui rendait la navigation confuse pour un
   * compte à plusieurs lieux : l'écran d'accueil parlait d'une salle choisie
   * dans une liste déroulante, sans jamais montrer les autres. La racine
   * répond maintenant « où ? », cet écran répond « quoi, maintenant ? ».
   */
  today: { fr: '/aujourdhui', en: '/today' },
  /* `/mes-lieux` reste servi : c'est le tableau de bord, sous son ancien nom.
     Des signets et des liens collés y pointent. */
  group: { fr: '/mes-lieux', en: '/venues' },
  calendar: { fr: '/calendrier', en: '/calendar' },
  /*
   * L'ÉQUIPE d'un lieu : ses postes et son planning, sur un seul écran.
   *
   * C'étaient deux endroits — le planning en navigation, les postes dans un
   * onglet des réglages — et l'état vide du premier renvoyait vers le second.
   * Une instruction qui traverse deux écrans est le signe que les deux n'en
   * font qu'un.
   */
  team: { fr: '/equipe', en: '/team' },
  /* MON planning suit la PERSONNE, pas le lieu : il reste à part, hors
     navigation, comme le compte. */
  myRoster: { fr: '/mon-planning', en: '/my-schedule' },
  bookings: { fr: '/reservations', en: '/bookings' },
  customers: { fr: '/clients', en: '/customers' },
  campaigns: { fr: '/campagnes', en: '/campaigns' },
  analytics: { fr: '/analytique', en: '/analytics' },
  experiences: { fr: '/experiences', en: '/experiences' },
  onboarding: { fr: '/onboarding', en: '/onboarding' },
  ops: { fr: '/exploitation', en: '/live-ops' },
  /*
   * LE PARC d'un lieu : ses casques, ses appareils, sa capacité vendable.
   *
   * Il était éclaté en trois : Exploitation → Matériel → Parc, Catalogue →
   * Ressources, et la console du studio. C'est le même objet — des casques
   * qu'on possède et qu'on vend — regardé depuis trois écrans qui ne se
   * citaient pas. La console garde sa vue de TOUS les lieux ; celle-ci est
   * la vue d'un lieu.
   */
  fleet: { fr: '/casques', en: '/headsets' },
  /* LES SALLES d'un lieu : tout ce qui se règle PAR SALLE — son serveur, sa
     clé, son réseau (dont le serveur XROAM), sa carte d'espace. */
  rooms: { fr: '/salles', en: '/rooms' },
  /* L'ACCUEIL : bornes d'enregistrement et écrans TV du lieu. */
  welcome: { fr: '/accueil', en: '/welcome' },
  scan: { fr: '/scan', en: '/scan' },
  settings: { fr: '/reglages', en: '/settings' },
  profile: { fr: '/profil', en: '/profile' },
  studio: { fr: '/studio', en: '/studio' },
} as const satisfies Record<string, Record<PathLocale, string>>

export type AppPath = keyof typeof APP_PATHS

/**
 * Les segments des chemins PUBLICS, sous `/v/:slug/`.
 *
 * Ce sont ceux qui comptent vraiment : ils voyagent hors de l'application, sur
 * du papier et dans des courriels. Les deux variantes restent servies pour
 * toujours — retirer l'ancienne est une décision qu'on ne prend pas sans
 * savoir combien de murs il faudrait repeindre.
 */
export const PUBLIC_SEGMENTS = {
  onboarding: { fr: 'enregistrement', en: 'registration' },
  unsubscribe: { fr: 'desabonnement', en: 'unsubscribe' },
} as const satisfies Record<string, Record<PathLocale, string>>

export type PublicSegment = keyof typeof PUBLIC_SEGMENTS

/**
 * Un lien TECHNIQUE : toujours en anglais, quelle que soit l'interface.
 *
 * La distinction est celle du destinataire, pas de la langue de travail. Un
 * lien de borne se colle dans le navigateur d'une tablette et vit ensuite dans
 * la configuration d'un appareil ; l'adresse d'un écran se tape sur une
 * télévision ; un aperçu d'éditeur se copie dans un billet. Ces adresses sont
 * lues par des machines et par des gens qui configurent, pas par des visiteurs
 * — et elles survivent au réglage de langue de celui qui les a créées. Une
 * borne installée par un anglophone puis reprise par une francophone garderait
 * sinon un `/enregistrement` que plus rien ne justifie.
 *
 * Ce qui reste TRADUIT est ce qu'un visiteur reçoit : le QR du mur, le lien de
 * reprise après un scan, le désabonnement d'un courriel. Là, la langue du
 * destinataire est la bonne.
 *
 * Les deux variantes restent servies dans tous les cas — c'est la règle du
 * fichier, et elle protège le papier déjà imprimé.
 */
export function technicalPublicSegment(key: PublicSegment): string {
  return PUBLIC_SEGMENTS[key].en
}

/**
 * Les surfaces à jeton, hors `/v/:slug/`. Toujours en anglais, même raison :
 * l'adresse est tapée une fois dans une télévision et n'en bouge plus.
 */
export const DEVICE_PATHS = {
  screen: { fr: '/ecran', en: '/screen' },
  /* `kiosk` est parti (28/10) : aucune route ne le servait. Une borne
     s'ouvre sur `/v/:slug/onboarding?kiosk=<jeton>`. */
} as const satisfies Record<string, Record<PathLocale, string>>

export function devicePath(key: keyof typeof DEVICE_PATHS): string {
  return DEVICE_PATHS[key].en
}

/** Le chemin à ÉMETTRE pour un écran, dans la langue demandée. */
export function appPath(key: AppPath, locale: string): string {
  const entry = APP_PATHS[key]
  return locale === 'en' ? entry.en : entry.fr
}

/** Toutes les variantes d'un écran — ce que le routeur doit accepter. */
export function appPathVariants(key: AppPath): string[] {
  const entry = APP_PATHS[key]
  /* `Set` parce que `/scan` et `/studio` sont identiques dans les deux
     langues, et déclarer deux fois la même route à react-router n'est pas une
     erreur mais du bruit. */
  return [...new Set([entry.fr, entry.en])]
}

/** Un segment public, dans la langue du destinataire. */
export function publicSegment(key: PublicSegment, locale: string): string {
  const entry = PUBLIC_SEGMENTS[key]
  return locale === 'en' ? entry.en : entry.fr
}

export function publicSegmentVariants(key: PublicSegment): string[] {
  const entry = PUBLIC_SEGMENTS[key]
  return [...new Set([entry.fr, entry.en])]
}

/**
 * L'ADRESSE PUBLIQUE d'un parcours d'enregistrement, absolue.
 *
 * Elle est écrite ici et pas dans les deux écrans qui l'affichent — la feuille
 * à imprimer de l'éditeur, et le code qu'on montre à la porte. Deux
 * constructions de la même URL, c'est la porte ouverte à ce que le mur et
 * l'écran mènent à deux endroits différents, et la seule façon de s'en
 * apercevoir serait qu'un visiteur le dise.
 *
 * Le `?o=` DÉSIGNE le parcours au lieu de laisser résoudre l'actif du lieu :
 * un opérateur qui a un parcours par expérience a besoin que l'affiche de la
 * salle A n'ouvre pas celle de la salle B.
 */
export function onboardingFormUrl(
  venueSlug: string,
  onboardingId: string,
  locale: string,
): string {
  return `${window.location.origin}/v/${venueSlug}/${publicSegment('onboarding', locale)}?o=${onboardingId}`
}
